import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { ISchoolDemographics } from '../../../shared/models/schoolDemographics';
import { SchoolDemographicsService } from '../../../shared/services/schoolDemographics.service';
import { PhoneFormatDirective } from '../../../shared/directives/phoneFormat.directive';
import { PhonePipe } from '../../../shared/pipes/phone.pipe';

@Component({
  selector: 'school-demographics',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent,
    PhoneFormatDirective,
    PhonePipe
  ],
  templateUrl: './school-demographics.component.html',
  styleUrl: './school-demographics.component.css'
})
export class SchoolDemographicsComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  // Left-column toggle: the two sub-documents of the School Demographics page.
  demographicsSections = [
    'School Demographics',
    'Size of School'
  ];
  selectedSection: string = this.demographicsSections[0];

  schoolDemographics: ISchoolDemographics = {};

  // Per-sub-document list of labeled fields to render on the right.
  sectionFields: Record<string, { key: keyof ISchoolDemographics; label: string; type?: 'phone' }[]> = {
    'School Demographics': [
      { key: 'name_of_school', label: 'Name of School:' },
      { key: 'administrator_private_phone', label: "Administrator's Private Phone Line:", type: 'phone' },
      { key: 'address', label: 'Address:' },
      { key: 'fax_number', label: 'Fax Number:', type: 'phone' },
      { key: 'city_state_zip', label: 'City, State, Zip:' },
      { key: 'main_telephone', label: 'Main Telephone Number:', type: 'phone' },
      { key: 'school_website', label: 'School website address:' },
    ],
    'Size of School': [
      { key: 'number_of_floors', label: 'Number of Floors:' },
      { key: 'number_of_exits', label: 'Number of Exits:' },
    ],
  };

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;

  constructor(
    private userDataService: UserDataService,
    private schoolDemographicsService: SchoolDemographicsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadSchoolDemographics();
  }

  selectSection(item: string): void {
    this.selectedSection = item;
  }

  loadSchoolDemographics(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.schoolDemographicsService.getSchoolDemographics(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.schoolDemographics = response.data || {};
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }

  onSubmit(): void {
    if (this.currentBuildingId && this.schoolDemographics) {
      this.loading = true;

      // Only send the fields for the selected sub-document, so saving one
      // doc never clears the other doc's values.
      const allowedKeys = new Set(this.sectionFields[this.selectedSection].map(f => f.key));
      const payload = Object.fromEntries(
        Object.entries(this.schoolDemographics)
          .filter(([key]) => allowedKeys.has(key as keyof ISchoolDemographics))
      );

      this.schoolDemographicsService.updateSchoolDemographics(this.currentBuildingId, payload).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating School Demographics.';
            this.loadSchoolDemographics();
          } else {
            this.messageModal = 'unsuccess';
            this.popupMessage = response.message;
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }
}
